/**
 * Salda's DENİZ KABUĞU — the scallop shell — in the shared 120×120 LINE asset box.
 *
 * One object across three assets, the shape this family likes best (Sakura's
 * blossom, Tallinn's flake, Ocean's jellyfish): the Home tab, all four passcode
 * dots and the chat wallpaper draw this drawing.
 *
 * WHY A SHELL AND NOT THE LAKE
 *
 * Salda's icon is a pure landscape — white shore, turquoise shallows, a low blue
 * range — and it has no object in it at all. That is a problem a tab glyph
 * cannot solve by tracing: a shoreline at 22px is two stripes, and every theme
 * in this family that tried to make a LANDSCAPE into a glyph ended up with one
 * (the flying tepui, and Everest's first pass before the summit was isolated).
 *
 * So the glyph is the thing you pick up off that shore. A scallop reads instantly
 * at any size, it is the one seashell whose silhouette nobody has to be told, and
 * it belongs to a white-sand lake in a way a starfish or an anchor would not. The
 * landscape keeps the menu strip, where it has 1472px to be a shoreline in.
 *
 * WHAT IS LOAD-BEARING
 *
 *  - THE FLUTED EDGE. Nine lobes around the rim, and they are the silhouette —
 *    a smooth fan is a hand-held screen or a paper fan, and the flutes are the
 *    single feature that says shell before anything else is legible.
 *  - THE PROPORTION, 92 across to 72 down. A real scallop is about 1.25 wide to
 *    tall. Drawn from a CIRCULAR sector it comes out near 2:1 and reads as a
 *    Japanese folding fan, which is why the sector here is ELLIPTICAL: a narrow
 *    horizontal radius against a tall vertical one.
 *  - THE EARS at the hinge. Two small tabs either side of the base, and they cost
 *    four points each. Without them the shape's bottom is a clean vertex, which
 *    is a fan's pivot; with them it is a bivalve's hinge.
 *  - THE RIBS RUN TO THE FLUTES, one rib per lobe. Ribs that land between the
 *    lobes read as scratches on the shell rather than as its structure.
 *
 * Every subpath is wound clockwise so a nonzero fill unions the overlaps instead
 * of punching holes in them ([[line-shared-glyph-modules]]).
 *
 * The fit: the shell spans x 13..107 and y 26..101, so it is WIDE — the opposite
 * of Ocean's jellyfish, and the sizePct correction in line-menu-button goes the
 * other way for it.
 */

const f = ( v: number ) => +v.toFixed( 1 );
const RAD = Math.PI / 180;

/** A polygon in clockwise order (y down), whichever order it arrives in. */
function cw( pts: [ number, number ][] ): string {
  let area = 0;
  for ( let i = 0; i < pts.length; i++ ) {
    const a = pts[ i ], b = pts[ ( i + 1 ) % pts.length ];
    area += a[ 0 ] * b[ 1 ] - b[ 0 ] * a[ 1 ];
  }
  const p = area < 0 ? pts.slice().reverse() : pts;
  return 'M' + p.map( ( q ) => `${ f( q[ 0 ] ) },${ f( q[ 1 ] ) }` ).join( ' L' ) + ' Z';
}

/* ---------------------------------------------------------------- geometry -- */

/** The hinge: where the two valves meet, and where every rib starts. */
const HX = 60, HY = 100;
/** The sector is ELLIPTICAL — see the proportion note above. */
const RX = 52, RY = 74;
/**
 * How far the fan opens either side of straight up.
 *
 * SIXTY-FOUR degrees, and the two passes it took to get there are the whole
 * lesson of this file. At 80 the rim's outer points fall almost level with the
 * hinge, so the shell's lower half is a wide flat trapezoid with a row of bumps
 * on top — a cloud on a plinth. At 64 the outer points sit a third of the way up,
 * which is where a scallop's widest point actually is.
 */
const SPREAD = 64;
/** One lobe per rib. Nine is the count a scallop reads at: at six it is a shell
 *  drawn by someone who has not looked at one, and at fourteen the flutes are
 *  under a pixel by the time LINE renders a 22px tab glyph. */
const RIBS = 9;
/** How far each flute bulges past the rim between two ribs. */
const LOBE = 5.5;

/** The rim point for an angle in degrees off vertical. */
function rim( deg: number, grow = 0 ): [ number, number ] {
  const a = deg * RAD;
  return [ HX + ( RX + grow ) * Math.sin( a ), HY - ( RY + grow ) * Math.cos( a ) ];
}

/** The angle of rib `i`, counted from the left edge. */
function ribAngle( i: number ): number {
  return -SPREAD + ( i / ( RIBS - 1 ) ) * ( SPREAD * 2 );
}

/**
 * The shell's outline: out of the hinge on a CONVEX flare, across the fluted rim,
 * and back down the other flare.
 *
 * The flares are the part that took two passes. Drawn as straight lines from the
 * hinge — which is what a sector gives you for free — the shell's sides are
 * ruled, and a scallop has no ruled edge anywhere on it. Each side is a cubic
 * that leaves the hinge going almost sideways and turns up into the rim, so the
 * widest part of the shell is a curve rather than a corner.
 *
 * The flutes are quadratics through a point pushed past the rim, one per gap
 * between ribs, so the rim scallops out and back in RIBS-1 times and every dip
 * lands exactly on a rib. That alignment is the difference between a shell and a
 * doily.
 */
export const SHELL_BODY = ( () => {
  const L = rim( -SPREAD ), R = rim( SPREAD );
  // control points: out of the hinge nearly horizontally, into the rim vertically
  const flareIn = ( p: [ number, number ] ): string =>
    ` C${ f( HX + ( p[ 0 ] - HX ) * 0.66 ) },${ f( HY - 2 ) }`
    + ` ${ f( p[ 0 ] - ( p[ 0 ] - HX ) * 0.06 ) },${ f( p[ 1 ] + 14 ) }`
    + ` ${ f( p[ 0 ] ) },${ f( p[ 1 ] ) }`;
  let d = `M${ f( HX ) },${ f( HY ) }` + flareIn( L );
  for ( let i = 0; i < RIBS - 1; i++ ) {
    // The flutes are deepest over the crown and shallower toward the ears, which
    // is what a real scallop does. Held at 0.78 minimum, not the 0.55 of the pass
    // before this one — at 0.55 the outer lobes flatten out entirely and the rim
    // stops being fluted exactly where the eye checks it against the side.
    const mid = ( ribAngle( i ) + ribAngle( i + 1 ) ) / 2;
    const taper = 0.78 + 0.22 * Math.cos( mid * RAD * 1.2 );
    const ctrl = rim( mid, LOBE * 1.5 * taper );
    const end = rim( ribAngle( i + 1 ) );
    d += ` Q${ f( ctrl[ 0 ] ) },${ f( ctrl[ 1 ] ) } ${ f( end[ 0 ] ) },${ f( end[ 1 ] ) }`;
  }
  // back down the right flare, reversed
  d += ` C${ f( R[ 0 ] - ( R[ 0 ] - HX ) * 0.06 ) },${ f( R[ 1 ] + 14 ) }`
    + ` ${ f( HX + ( R[ 0 ] - HX ) * 0.66 ) },${ f( HY - 2 ) }`
    + ` ${ f( HX ) },${ f( HY ) }`;
  return `${ d } Z`;
} )();

/**
 * The two ears at the hinge — small wings that poke just past the flare.
 *
 * They exist to give the base a hinge instead of a point, and they are the third
 * thing this drawing got wrong before it got right. Hung out level with the hinge
 * they read as two feet under the shell; tucked entirely inside the flare they
 * are invisible. They sit at y 90, where the flare has reached about 22 units
 * out, and reach to 31 — eight or nine units of wing, which is one pixel at tab
 * size and the right amount there.
 */
export const SHELL_EARS = [
  // Their inner points MEET on the centre line, at x = HX exactly.
  //
  // This took three tries and each failure was the same failure. At +-6 the ears
  // met the flare at a tangent and the join showed as a step. At +-3 the step went
  // but a small V opened under the hinge, because the two ears stopped short of
  // each other and nothing filled the six units between them. At 0 they share the
  // centre line, the silhouette closes, and the hinge is one unbroken edge.
  //
  // A GAP in a flat two-colour glyph is worse than a wrong colour: a colour is a
  // decision anyone can argue with, and a hole is a mistake.
  cw( [ [ HX, HY - 3 ], [ HX - 31, HY - 11 ], [ HX - 27, HY - 3 ], [ HX, HY + 1 ] ] ),
  cw( [ [ HX, HY - 3 ], [ HX + 31, HY - 11 ], [ HX + 27, HY - 3 ], [ HX, HY + 1 ] ] ),
].join( ' ' );

/**
 * The ribs: one tapered wedge per lobe, running from just above the hinge out to
 * the rim.
 *
 * They stop SHORT of the rim by three units. A rib drawn all the way out crosses
 * the fluted edge and puts a notch in the silhouette at exactly the size where
 * the silhouette is all there is.
 */
export const SHELL_RIBS = ( () => {
  const out: string[] = [];
  for ( let i = 0; i < RIBS; i++ ) {
    const a = ribAngle( i );
    // and they run NEARER the rim — two units short instead of three, so each
    // groove reaches the flute it belongs to. A rib that stops early reads as a
    // scratch; one that lands on its own lobe reads as structure.
    const tip = rim( a, -2 );
    const nrm = a * RAD + Math.PI / 2;
    // the wedge widens as it goes out, the way a rib on a shell does
    // WIDER than the first pass, which ran 1.6 to 4.4 and left grooves a third of
    // a pixel wide once LINE renders a 30px dot. The owner's note was that the
    // shell read as a flat fan rather than as a fluted one, and a groove nobody
    // can see is a groove that is not there.
    const w0 = 2.6, w1 = 6.4;
    // The grooves START at radius 15, not 8. Converging on the hinge they piled
    // into a solid wedge at the bottom of the shell and the whole lower third went
    // dark — nine wedges meeting at a point is a blot, however fine each one is.
    const bx = HX + Math.sin( a * RAD ) * 15, by = HY - Math.cos( a * RAD ) * 15;
    out.push( cw( [
      [ bx + Math.cos( nrm ) * w0 / 2, by + Math.sin( nrm ) * w0 / 2 ],
      [ tip[ 0 ] + Math.cos( nrm ) * w1 / 2, tip[ 1 ] + Math.sin( nrm ) * w1 / 2 ],
      [ tip[ 0 ] - Math.cos( nrm ) * w1 / 2, tip[ 1 ] - Math.sin( nrm ) * w1 / 2 ],
      [ bx - Math.cos( nrm ) * w0 / 2, by - Math.sin( nrm ) * w0 / 2 ],
    ] ) );
  }
  return out.join( ' ' );
} )();

/** The whole shell as ONE path — body and ears. This is what the idle tab and
 *  the unentered passcode dot draw: an unlit tab is a silhouette, the rule every
 *  theme's Home glyph in this family follows. */
export const SHELL = `${ SHELL_BODY } ${ SHELL_EARS }`;

/**
 * A SIMPLER shell for the wallpaper's smallest grains: five lobes instead of
 * nine, and no ears.
 *
 * Same reason Tallinn's simple flake and Ocean's simple jellyfish exist. At a
 * third of this box a nine-lobe rim puts each flute under a pixel and they fill
 * in, so the shell comes back as a plain fan — which is worse than a shell drawn
 * honestly with fewer flutes.
 */
export const SHELL_SIMPLE = ( () => {
  const N = 5;
  const ang = ( i: number ) => -SPREAD + ( i / ( N - 1 ) ) * ( SPREAD * 2 );
  const L = rim( -SPREAD ), R = rim( SPREAD );
  let d = `M${ f( HX ) },${ f( HY ) }`
    + ` C${ f( HX + ( L[ 0 ] - HX ) * 0.66 ) },${ f( HY - 2 ) }`
    + ` ${ f( L[ 0 ] - ( L[ 0 ] - HX ) * 0.06 ) },${ f( L[ 1 ] + 14 ) }`
    + ` ${ f( L[ 0 ] ) },${ f( L[ 1 ] ) }`;
  for ( let i = 0; i < N - 1; i++ ) {
    const mid = ( ang( i ) + ang( i + 1 ) ) / 2;
    // 1.6, not the 2.1 this started on. Five lobes pushed that far out stop
    // reading as a rim and start reading as a cloud — which is what they did on
    // Salda's own beach before the full shell took that job back.
    const ctrl = rim( mid, LOBE * 1.6 * ( 0.8 + 0.2 * Math.cos( mid * RAD * 1.2 ) ) );
    const end = rim( ang( i + 1 ) );
    d += ` Q${ f( ctrl[ 0 ] ) },${ f( ctrl[ 1 ] ) } ${ f( end[ 0 ] ) },${ f( end[ 1 ] ) }`;
  }
  d += ` C${ f( R[ 0 ] - ( R[ 0 ] - HX ) * 0.06 ) },${ f( R[ 1 ] + 14 ) }`
    + ` ${ f( HX + ( R[ 0 ] - HX ) * 0.66 ) },${ f( HY - 2 ) }`
    + ` ${ f( HX ) },${ f( HY ) }`;
  return `${ d } Z`;
} )();

/* ------------------------------------------------------- passcode colours --
 * Held here rather than in the passcode component: line-lock (the preview the
 * marketplace screenshot shows) and line-page (the exported PNGs) both read
 * them, and every time this family kept two copies of a colour they drifted.
 */

/**
 * The four entered shells, and they are the colours a SEA SHELL actually is:
 * cream, warm beige, a pearly pink and a deeper sand. Nothing else.
 *
 * THE GROUND MOVED TWICE, and that is the whole story of this constant. The
 * first set coloured the shells in the LAKE — pale turquoise, lake blue, deep
 * teal — which turned a shell into a turquoise fan. The second set made them
 * pale shell colours and, because a cream shell cannot be seen on a pale sand
 * zone, FLIPPED the passcode screen to put the deep lake on top.
 *
 * The owner's screenshots (2026-08-15) ended that: the phone draws the ordinary
 * light-skin arrangement, CREAM on top and the lake below. So the flip is gone
 * and, measured on the sampled cream (#FFF8E8), the pale set came out at 1.04 /
 * 1.22 / 1.39 / 1.58 — the first shell invisible — while the unentered one sat at
 * 2.11 as the heaviest mark on the screen. Fully inverted, the same failure Ocean
 * shipped on the same day.
 *
 * So the shells are darker than the beach now, which is what a shell on sand
 * actually is: warm sand, blush, taupe and a deeper sand, measured on the zone at
 * 1.89 / 2.01 / 2.04 / 2.12.
 *
 * That is a NARROW band and it is deliberate. Correcting the inversion first
 * overshot to 2.41..3.42 and the owner called three of the four too dark, the
 * same overcorrection Ocean paid four rounds for on the same day. On a near-white
 * cream a mark at 2.0 is already plainly a shell; there is no need to go to 3.
 *
 * The BLUSH also needed a hue pass. Deepening it toward a shell's pink first
 * landed on #C57F77 and the owner read it as plainly RED — at this size a
 * saturated warm pink beside three sand tones stops being a shell and becomes the
 * odd bright mark in the row. It is greyed toward mauve, which is what the pink
 * inside a real bivalve actually is.
 */
export const SHELL_PC_COLORS = [ '#D6B184', '#D3A8A2', '#C9AB94', '#C8A87E' ];

/**
 * ONE tone for every unentered shell — the frame never changes per digit (the
 * rule Aurora's orbs settled).
 *
 * A PALE SAND, 1.21:1 on the sampled cream zone: one quiet step off the beach it
 * is lying on. It moved IN as the picked-up shells were lightened — with the lit
 * row down at 1.89..2.12 there was no other way to keep a filled slot plainly
 * heavier than an empty one. It is the only one of the five that is not a picked-up shell
 * colour, and that is the point — an unentered dot is the shell you have not
 * picked up yet.
 *
 * On a CREAM zone the quiet direction is the only one available. Ocean could make
 * its empty dot paler than its mid-teal zone; there is nothing paler than this
 * ground, so this one goes a step darker and stops well under the picked-up
 * shells' 2.41..3.42. Going further down is the trap: at that point it becomes a
 * fifth shell rather than an empty slot.
 */
export const SHELL_PC_OFF = '#EFE2C8';

/**
 * A small per-digit lean, alternating side, about the HINGE rather than the
 * centre — a shell lying on sand pivots where it rests.
 *
 * Kept small for a different reason from the flake's. A flake at 20 degrees is
 * the same picture; a shell at 20 degrees is a shell that has been knocked over,
 * and four of them at four angles reads as debris rather than as a set.
 */
export const SHELL_PC_TILTS = [ -11, 8, -6, 13 ];
